import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "components/ui/sheet";
import { Button } from "components/ui/button";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle,
  Clock,
  PencilSimple,
  Receipt,
  Trash,
} from "@phosphor-icons/react";
import {
  BucketTransactionType,
  BudgetBucketView,
} from "screens/investment/types/budget-bucket";
import { useBucketTransactions } from "screens/investment/hooks/useBudgetBuckets";
import { BudgetBucketFormModal } from "./BudgetBucketFormModal";

interface BudgetBucketDrawerProps {
  bucket: BudgetBucketView | null;
  onClose: () => void;
  onUpdate: (id: number, dto: any) => Promise<unknown>;
  onToggleApproval: (id: number, isApproved: boolean) => Promise<unknown>;
  onDelete: (id: number) => Promise<unknown>;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const TRANSACTION_LABELS: Record<BucketTransactionType, string> = {
  WITHDRAW: "Onttrokken (investeringslijn)",
  RELEASE: "Vrijgegeven naar strategisch budget",
  UNFORESEEN_WITHDRAW: "Onvoorzien onttrokken",
  ADJUSTMENT: "Aanpassing",
};

const isOutflow = (type: BucketTransactionType) =>
  type === BucketTransactionType.WITHDRAW ||
  type === BucketTransactionType.UNFORESEEN_WITHDRAW;

/**
 * Side drawer with bucket details, header KPIs and the full audit log.
 * The audit log is the source of truth for the bucket balance, so this
 * drawer doubles as the explanation page when a PM wants to know "where did
 * my budget go?".
 */
export const BudgetBucketDrawer = ({
  bucket,
  onClose,
  onUpdate,
  onToggleApproval,
  onDelete,
}: BudgetBucketDrawerProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { transactions, isLoading } = useBucketTransactions(bucket?.id ?? null);

  const handleDelete = async () => {
    if (!bucket) return;
    if (
      !window.confirm(
        `Pot "${bucket.name}" verwijderen? Investeringslijnen die hieraan gekoppeld zijn moeten eerst losgekoppeld worden.`,
      )
    ) {
      return;
    }
    await onDelete(bucket.id);
    onClose();
  };

  return (
    <>
      <Sheet open={!!bucket} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-4 sm:max-w-xl"
        >
          {bucket && (
            <>
              <SheetHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <SheetTitle className="truncate text-left">
                      {bucket.name}
                    </SheetTitle>
                    <SheetDescription className="text-left">
                      {bucket.year ? `${bucket.year} · ` : ""}
                      Strategisch budget · {bucket.investmentItemCount} investeringslijn
                      {bucket.investmentItemCount === 1 ? "" : "en"}
                    </SheetDescription>
                  </div>
                  <span
                    className={`flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                      bucket.isApproved
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-amber-200 bg-amber-50 text-amber-700"
                    }`}
                  >
                    {bucket.isApproved ? (
                      <CheckCircle weight="fill" className="h-3 w-3" />
                    ) : (
                      <Clock weight="fill" className="h-3 w-3" />
                    )}
                    {bucket.isApproved ? "Goedgekeurd" : "Niet goedgekeurd"}
                  </span>
                </div>
              </SheetHeader>

              {/* KPI strip */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
                  <p className="text-[10px] font-medium uppercase text-neutral-secondary-mid">
                    Totaal
                  </p>
                  <p className="text-base font-semibold text-neutral-primary">
                    {formatCurrency(bucket.totalAmount)}
                  </p>
                </div>
                <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
                  <p className="text-[10px] font-medium uppercase text-neutral-secondary-mid">
                    Gebruikt
                  </p>
                  <p className="text-base font-semibold text-neutral-primary">
                    {formatCurrency(bucket.usedAmount)}
                  </p>
                </div>
                <div
                  className={`rounded-md border p-3 ${
                    bucket.remainingAmount < 0
                      ? "border-red-200 bg-red-50"
                      : "border-moss-primary/30 bg-moss-primary/5"
                  }`}
                >
                  <p className="text-[10px] font-medium uppercase text-neutral-secondary-mid">
                    Resterend
                  </p>
                  <p
                    className={`text-base font-semibold ${
                      bucket.remainingAmount < 0
                        ? "text-red-700"
                        : "text-moss-primary"
                    }`}
                  >
                    {formatCurrency(bucket.remainingAmount)}
                  </p>
                </div>
              </div>

              {bucket.notes && (
                <div className="rounded-md border border-neutral-200 bg-white p-3 text-sm text-neutral-700">
                  {bucket.notes}
                </div>
              )}

              {/* Action row */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsEditOpen(true)}
                >
                  <PencilSimple className="mr-1 h-3.5 w-3.5" />
                  Bewerken
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    onToggleApproval(bucket.id, !bucket.isApproved)
                  }
                >
                  {bucket.isApproved
                    ? "Goedkeuring intrekken"
                    : "Goedkeuren"}
                </Button>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  onClick={handleDelete}
                  disabled={bucket.investmentItemCount > 0}
                  title={
                    bucket.investmentItemCount > 0
                      ? "Verwijder eerst alle investeringslijnen uit dit strategisch budget"
                      : "Verwijder strategisch budget"
                  }
                >
                  <Trash className="mr-1 h-3.5 w-3.5" />
                  Verwijderen
                </Button>
              </div>

              {/* Audit log */}
              <div className="flex min-h-0 flex-1 flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Receipt
                    weight="duotone"
                    className="h-4 w-4 text-moss-primary"
                  />
                  <h3 className="text-sm font-semibold text-neutral-primary">
                    Audit log
                  </h3>
                  <span className="text-xs text-neutral-secondary-mid">
                    ({transactions.length})
                  </span>
                </div>

                <div className="flex-1 overflow-auto rounded-md border border-neutral-200">
                  {isLoading ? (
                    <div className="flex items-center justify-center p-6 text-sm text-neutral-secondary-mid">
                      Laden...
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="flex items-center justify-center p-6 text-sm text-neutral-secondary-mid">
                      Nog geen mutaties op dit strategisch budget.
                    </div>
                  ) : (
                    <ul className="divide-y divide-neutral-200">
                      {transactions.map((tx) => {
                        const outflow = isOutflow(tx.type);
                        return (
                          <li
                            key={tx.id}
                            className="flex items-start gap-3 p-3"
                          >
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                                outflow
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-emerald-100 text-emerald-700"
                              }`}
                            >
                              {outflow ? (
                                <ArrowUp weight="bold" className="h-4 w-4" />
                              ) : (
                                <ArrowDown weight="bold" className="h-4 w-4" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <p className="truncate text-sm font-medium text-neutral-primary">
                                  {TRANSACTION_LABELS[tx.type]}
                                </p>
                                <p
                                  className={`shrink-0 text-sm font-semibold ${
                                    outflow
                                      ? "text-amber-700"
                                      : "text-emerald-700"
                                  }`}
                                >
                                  {outflow ? "-" : "+"}
                                  {formatCurrency(Math.abs(Number(tx.amount)))}
                                </p>
                              </div>
                              {(tx.investmentItemTitle ||
                                tx.projectPartTitle) && (
                                <p className="truncate text-xs text-neutral-secondary-mid">
                                  {tx.investmentItemTitle
                                    ? `Investeringslijn: ${tx.investmentItemTitle}`
                                    : `Projectonderdeel: ${tx.projectPartTitle}`}
                                </p>
                              )}
                              {tx.reason && (
                                <p className="text-xs text-neutral-secondary-mid">
                                  {tx.reason}
                                </p>
                              )}
                              <p className="text-[10px] text-neutral-secondary-mid">
                                {new Date(tx.createdAt).toLocaleString(
                                  "nl-BE",
                                )}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {bucket && (
        <BudgetBucketFormModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onUpdate={async (id, dto) => {
            await onUpdate(id, dto);
            setIsEditOpen(false);
          }}
          buildingId={bucket.buildingId}
          bucket={bucket}
        />
      )}
    </>
  );
};
